// A card needs attention when its status bar is yellow (warning) or red
// (error), the same colours the service sections have always used.
export const ATTENTION_STATUS_COLORS = ['yellow', 'red'];

const isLoaded = (service) => !service?.__isSkeleton;
const needsAttention = (color) => ATTENTION_STATUS_COLORS.includes(color);

/**
 * (3133/3153/3157) What the service sections tell the rest of the dashboard:
 * - attentionTitles: sections to pulse while collapsed (3157)
 * - servicesNeedingAttention: warning or error services, and app auths that
 *   are only warning, for "Attention required" and its notification
 * - failedAppAuths: app auths in error, for the App auth failure notification
 *
 * @param {object} groupedServices - DashboardAccordion's sections by title
 * @param {(service: object) => string | undefined} getServiceColor
 * @param {(authService: object) => string | undefined} getAuthColor
 */
export const summariseServiceHealth = (
    groupedServices,
    getServiceColor,
    getAuthColor,
) => {
    const attentionTitles = [];
    let servicesNeedingAttention = 0;
    let failedAppAuths = 0;

    Object.entries(groupedServices || {}).forEach(([title, section]) => {
        const serviceColors = (section?.services || [])
            .filter(isLoaded)
            .map(getServiceColor);
        const authColors = (section?.authServices || [])
            .filter(isLoaded)
            .map(getAuthColor);

        if ([...serviceColors, ...authColors].some(needsAttention)) {
            attentionTitles.push(title);
        }
        servicesNeedingAttention +=
            serviceColors.filter(needsAttention).length +
            authColors.filter((color) => color === 'yellow').length;
        failedAppAuths += authColors.filter((color) => color === 'red').length;
    });

    return { attentionTitles, servicesNeedingAttention, failedAppAuths };
};

/** Whether two summaries would show the same thing, to skip no-op updates. */
export const isSameServiceHealth = (a, b) =>
    a?.servicesNeedingAttention === b?.servicesNeedingAttention &&
    a?.failedAppAuths === b?.failedAppAuths &&
    a?.attentionTitles?.join('|') === b?.attentionTitles?.join('|');
